import { identity, sumBy } from 'lodash';
import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { OTHER_COLOR } from '../../constants/colors';
import { ItemContextProvider } from '../../contexts/ItemContext';
import DropdownButton from '../basics/DropdownButton';
import Endash from '../basics/Endash';
import ItemsTable, { TableWrapper } from '../tables/ItemsTable';
import ItemsBarChart from './ItemsBarChart';
import LegendSwatch from './LegendSwatch';
import { getItemColor } from '../../utils/colors';
import { useFarmName } from '../../utils/hooks';

const ItemsList = styled(ItemsTable)`
  height: auto;
  max-height: calc(100% - 35px);
  ${TableWrapper} {
    // Since this doesn't have a fixed header we can scroll the table wrapper.
    // This allows us to use display: table so we can make the table element full width.
    overflow: auto;
    // Make sure the table wrapper is on top of the chart, which has some overflow empty space.
    z-index: 1;
    height: auto;
    max-height: 100%;
  }
  table {
    width: 100%;
    display: table;
    border-collapse: collapse;
    border: none;
    margin-bottom: 0;
  }
  th {
    display: none;
  }
  td {
    padding: var(--cell-padding);
    width: 80%;
    max-width: 150px;
    border: none;
    border-bottom: 0.5px solid var(--border-color);
    &:last-of-type {
      text-align: right;
      width: 20%;
    }
    &:only-child {
      // Center the No items message
      text-align: center;
    }
    border-right: none;
  }
`;

const NoItemsDash = styled.div`
  height: 30px;
`;

const ViewMoreLink = styled.div`
  border-top: 1px solid var(--border-color);
`;

const ITEM_COLUMN = {
  accessor: 'name',
  // eslint-disable-next-line react/display-name
  format: (name, { color }) => (
    <div className="d-flex align-items-center overflow-hidden w-100">
      <LegendSwatch className="mr-2 flex-shrink-0" color={color} />
      <div className="flex-shrink-1 text-truncate" title={name}>
        {name}
      </div>
    </div>
  ),
};

const SummaryTopContent = styled(
  ({
    items,
    dropdownProps,
    secondaryDropdownProps,
    csvButton,
    accessor,
    aggregationInfo,
    formattedTotalValue,
    className,
  }) => (
    <div className={className}>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <div className="d-flex">
            <DropdownButton
              size="sm"
              color="light"
              className="ml-n2"
              showOverflow
              {...dropdownProps}
            />
            {secondaryDropdownProps && (
              <DropdownButton
                size="sm"
                color="light"
                showOverflow
                {...secondaryDropdownProps}
              />
            )}
          </div>
          <div className="text-muted font-tiny mb-1">
            {aggregationInfo}: {formattedTotalValue}
          </div>
        </div>
        {csvButton}
      </div>
      {items.length === 0 ? (
        <NoItemsDash className="text-center text-muted font-xxl">
          <Endash />
        </NoItemsDash>
      ) : (
        <ItemsBarChart items={items} accessor={accessor} />
      )}
    </div>
  ),
)`
  padding: 0 var(--cell-padding);
`;

// Max items shown, including the "Other" item
const MAX_ITEMS = 6;

const ItemsSummaryList = ({
  items, // Must be sorted by value
  dropdownProps,
  secondaryDropdownProps,
  valueColumn,
  aggregationInfo,
  viewMoreLink,
}) => {
  const { format = identity, accessor } = valueColumn;
  const farmName = useFarmName();
  const { summaryItems, chartItems, chartKey, totalValue } = useMemo(() => {
    const itemsWithOther = (items.length > MAX_ITEMS
      ? [
          ...items.slice(0, MAX_ITEMS - 1),
          {
            id: 'other',
            name: 'Other',
            color: OTHER_COLOR,
            [accessor]: sumBy(items.slice(MAX_ITEMS - 1), accessor),
          },
        ]
      : items
    ).map((item, i) => ({
      ...item,
      color:
        typeof item.color === 'string' ? item.color : getItemColor(item, i),
    }));
    const totalItemValue = sumBy(items, accessor);
    return {
      summaryItems: itemsWithOther,
      chartItems: itemsWithOther.filter(
        // Don't show items with a value less than 1.5% of the total in the chart.
        (item) => item[accessor] / totalItemValue > 0.015,
      ),
      chartKey: nanoid(),
      totalValue: totalItemValue,
    };
  }, [items, accessor]);

  const valueColumnWithDisplayString = {
    displayString: `${aggregationInfo}${
      valueColumn.unit ? ` (${valueColumn.unit})` : ''
    }`,
    ...valueColumn,
  };

  const tableHeader = ({ csvButton }) => (
    <SummaryTopContent
      // Key is used to avoid using the same chart instance but changing the data,
      // which causes an error.
      key={chartKey}
      items={chartItems}
      csvButton={csvButton}
      dropdownProps={dropdownProps}
      secondaryDropdownProps={secondaryDropdownProps}
      accessor={accessor}
      aggregationInfo={aggregationInfo}
      formattedTotalValue={format(totalValue, summaryItems[0] || {})}
    />
  );

  return (
    // Scope the ItemContext to this component so it doesn't affect other components.
    <ItemContextProvider>
      <ItemsList
        tableHeader={tableHeader}
        className="w-100"
        data={summaryItems}
        cols={[ITEM_COLUMN, valueColumnWithDisplayString]}
        csvFileName={[dropdownProps.selectedOption, 'summary', farmName].join(
          ' - ',
        )}
        scrollProps={{ block: 'nearest' }}
      />
      <ViewMoreLink className="p-2 w-100">
        <Link to={viewMoreLink} className="font-small text-primary">
          More details
        </Link>
      </ViewMoreLink>
    </ItemContextProvider>
  );
};

export default ItemsSummaryList;
